import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn, Field, useToast } from '@/components/ui';
import { createProjectCreateRequest } from '@/api/requests';
import { adminCreateProject } from '@/api/projects';
import type { ProjectCreatePayload } from '@/api/projects';
import { getErrorMessage } from '@/api';
import { emailLocal } from '@/lib/format';
import { useMetaContext } from '@/components/Layout';

import CommonInfoCard from './projectDetail/CommonInfoCard';
// 데이터 연동 정보 카드는 현재 표시할 항목이 없어 비활성화. tapjoy_app_id는 '플랫폼별 정보'로 이동.
// import DataLinkCard from './projectDetail/DataLinkCard';
import PlatformInfoCard from './projectDetail/PlatformInfoCard';
import { emptyPlatformState, platformLabel } from './projectDetail/infoTabFields';
import { useNetworkLookup } from './projectDetail/useNetworkLookup';
import type { CommonState, PlatformState } from './projectDetail/infoTabFields';

import styles from './ProjectNew.module.css';
import infoStyles from './projectDetail/InfoTab.module.css';

// plan §5.26 / §5.15: PageProjectNew → POST /api/requests/project-create.
// 프로젝트 정보 수정(InfoTab)과 동일한 CommonInfoCard / PlatformInfoCard를 재사용해
// 입력 폼의 룩앤필과 검증 동작을 통일한다. 신규는 project_id가 미부여라 자동 부여로 표시.

// 새 프로젝트 기본값 — publisher / company는 supercent로 미리 채운다 (수정 가능).
const DEFAULT_ORG = 'supercent';
const EMPTY_COMMON: CommonState = {
  display_name: '',
  app_match_name: '',
  ga_game_id: '',
  tapjoy_app_id: '',
  adjust_app_token: '',
  gcp_project_id: '',
  publisher: DEFAULT_ORG,
  company: DEFAULT_ORG,
};

// 새 프로젝트는 기본 AOS/iOS 둘 다 추가된 상태로 시작 (기존 폼의 os 기본값과 동일).
const DEFAULT_CODES = ['aos', 'ios'];

export default function ProjectNewPage() {
  const meta = useMetaContext();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = meta.current_user.platform_role === 'admin';

  const [common, setCommon] = useState<CommonState>(EMPTY_COMMON);
  const [platforms, setPlatforms] = useState<Record<string, PlatformState>>(() => ({
    aos: emptyPlatformState(),
    ios: emptyPlatformState(),
  }));
  // 새 프로젝트의 모든 platform은 "추가 중"(아직 미등록) — InfoTab의 addingCodes 의미와 동일.
  const [addingCodes, setAddingCodes] = useState<string[]>(DEFAULT_CODES);
  const [activeCode, setActiveCode] = useState<string>('aos');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setCommonField = <K extends keyof CommonState>(key: K, value: CommonState[K]) => {
    setCommon((prev) => ({ ...prev, [key]: value }));
  };

  const setPlatformField = (code: string, key: keyof PlatformState, value: string) => {
    setPlatforms((prev) => ({ ...prev, [code]: { ...prev[code], [key]: value } }));
  };

  const { lookingUpNetwork, lookupNetwork, lookupAllNetworks } = useNetworkLookup(platforms, setPlatformField);

  // + 메뉴로 OS 추가 — 로컬 state에만 platform 생성.
  const handlePickAdd = (code: string) => {
    setAddingCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setPlatforms((prev) => (prev[code] ? prev : { ...prev, [code]: emptyPlatformState() }));
    setActiveCode(code);
    toast.info(`${platformLabel(code)} 추가됨`);
  };

  // 탭 제거(x) — 최소 1개 OS는 유지.
  const handleRemove = (code: string) => {
    if (addingCodes.length <= 1) {
      toast.error('최소 하나의 OS는 필요해요');
      return;
    }
    const remaining = addingCodes.filter((c) => c !== code);
    setAddingCodes(remaining);
    setPlatforms((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
    setActiveCode((cur) => (cur === code ? remaining[0] : cur));
  };

  // form → canonical payload (stores 배열). admin 직접 생성과 비-admin 요청이 공유하는
  // 단일 빌더 — 두 경로가 같은 형태를 보내 승인 시에도 동일하게 처리되도록 한다.
  const buildProjectPayload = (): ProjectCreatePayload => {
    const stores: ProjectCreatePayload['stores'] = [];
    for (const code of addingCodes) {
      const p = platforms[code];
      if (!p) continue;
      stores.push({
        code,
        // bundle_id / ga_game_id / tapjoy_app_id는 플랫폼(aos/ios)별 값 — 플랫폼별 정보 카드에서 입력.
        bundle_id: p.bundle_id || null,
        store_id: p.store_id || null,
        sku: code === 'ios' ? p.sku || null : null,
        // ga_game_id는 필수 — 비어 있으면 NULL 대신 '0' 기록 (백엔드도 동일 폴백).
        ga_game_id: p.ga_game_id.trim() || '0',
        tapjoy_app_id: p.tapjoy_app_id || null,
        state_code: p.state_code || '1',
      });
    }
    return {
      display_name: common.display_name,
      app_match_name: common.app_match_name,
      gcp_project_id: common.gcp_project_id || null,
      stores,
      ga_game_id: common.ga_game_id || null,
      tapjoy_app_id: common.tapjoy_app_id || null,
      adjust_app_token: common.adjust_app_token || null,
      publisher: common.publisher || null,
      company: common.company || null,
    };
  };

  const submit = async () => {
    if (addingCodes.length === 0) {
      toast.error('적어도 하나의 OS를 선택해주세요');
      return;
    }
    // 필수값 검증 — 비어 있으면 어떤 항목이 비었는지 명시해 경고하고 생성을 막는다.
    const missing: string[] = [];
    if (!common.display_name.trim()) missing.push('display_name');
    if (!common.app_match_name.trim()) missing.push('app_match_name');
    for (const code of addingCodes) {
      const p = platforms[code];
      if (!p?.store_id?.trim()) missing.push(`${platformLabel(code)} store_id`);
      if (!p?.bundle_id?.trim()) missing.push(`${platformLabel(code)} bundle_id`);
      if (code === 'ios' && !p?.sku?.trim()) missing.push('iOS sku');
    }
    // admin 경로에서는 reason이 필수가 아님 — request flow를 거치지 않음.
    if (!isAdmin && !reason.trim()) missing.push('요청 사유');
    if (missing.length > 0) {
      toast.error(`필수값이 비어있어요: ${missing.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      // admin·비-admin 모두 동일한 canonical payload(stores 배열)를 만든다.
      const payload = buildProjectPayload();

      if (isAdmin) {
        // admin: BIGQUERY_APPS_TABLE에 즉시 INSERT (1~5초 동기).
        const response = await adminCreateProject(payload);
        toast.success(`프로젝트 생성 완료 — project_id=${response.project_id}`);
        setTimeout(() => navigate('/'), 600);
        return;
      }

      // 비-admin: 동일 payload를 request flow로. 승인 시 admin 직접 생성과 같은 경로로 처리된다.
      // 요청 봉투의 payload는 freeform JSON(Record)이라 canonical 타입을 그대로 담는다.
      await createProjectCreateRequest({
        display_name: common.display_name,
        payload: payload as unknown as Record<string, unknown>,
        reason,
      });
      toast.success('새 프로젝트 요청이 전송되었어요. 운영자가 utils.apps 등록을 완료해야 프로젝트 목록에 나타납니다.');
      setTimeout(() => navigate('/'), 600);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = submitting ? '전송 중...' : isAdmin ? '프로젝트 생성' : '요청 보내기';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>새 프로젝트 요청</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="default" onClick={() => navigate('/')}>
            취소
          </Btn>
          <Btn variant="primary" onClick={submit} disabled={submitting}>
            {submitLabel}
          </Btn>
        </div>
      </div>

      {/* 공통 정보 — 수정 폼과 동일 카드. project_id는 미부여라 자동 부여 표시. */}
      <CommonInfoCard projectId="" common={common} ver={0} onChangeField={setCommonField} />

      {/* 데이터 연동 정보 — 현재 표시할 항목 없어 비활성화(tapjoy_app_id는 플랫폼별 정보로 이동)
      <DataLinkCard common={common} ver={0} onChangeField={setCommonField} /> */}

      {/* 플랫폼별 정보 — 수정 폼과 동일 탭 UI. 비-admin도 OS 추가/제거 가능. */}
      <PlatformInfoCard
        allPlatformCodes={addingCodes}
        activeCode={activeCode}
        onSelectCode={setActiveCode}
        platforms={platforms}
        addingCodes={addingCodes}
        isAdmin={isAdmin}
        canAdd
        onRemove={handleRemove}
        ver={0}
        forceLockable
        showOverlay={false}
        hasActiveInitial={false}
        onPickAdd={handlePickAdd}
        onChangeField={setPlatformField}
        onLookupNetwork={lookupNetwork}
        lookingUpNetwork={lookingUpNetwork}
        onLookupAllNetworks={lookupAllNetworks}
      />

      {/* 요청 사유 + 제출 — 변경 폼 ChangeSummaryCard의 카드/actionRow 스타일 공유. */}
      <div className={infoStyles.card}>
        <Field
          label="요청 사유"
          required={!isAdmin}
          help="관리자 검토 시 참고됩니다"
          textarea
          rows={4}
          placeholder="이 프로젝트를 생성해야 하는 배경 / 출시 일정 / 담당 등을 적어주세요"
          value={reason}
          onChange={setReason}
        />
        <div className={infoStyles.actionRow}>
          <div style={{ flex: 1, font: '500 12px/1.5 "Pretendard"', color: 'var(--sc-text-muted)' }}>
            요청자: <b style={{ color: 'var(--sc-text)' }}>{emailLocal(meta.current_user.email)}</b>
          </div>
          <Btn variant="default" onClick={() => navigate('/')}>
            취소
          </Btn>
          <Btn variant="primary" onClick={submit} disabled={submitting}>
            {submitLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}
